import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ChatAttachment, ChatConfigRequest, ChatOption, Message, MessageData, MessageMetadata } from '../../../models/chat.models';
import { ChatEvent } from '../../../models/event.models';
import { MarkdownService } from 'ngx-markdown';
import { SpeechRecognizer, SpeechSynthesizer, AudioConfig, ResultReason, SpeakerAudioDestination } from "microsoft-cognitiveservices-speech-sdk";
import { ChatStatus, FeedbackState, IdleStatusType, LinkType, ReservedTags, Role, SkillType } from '../../../enums/chat.enum';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Observable, Subject } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import * as uuid from 'uuid';
import { ChatStreamService } from '../../../services/chat-stream.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthenticationService } from '../../../services/authentication.service';
import { HostRoute } from '../../../enums/subscription-enum';
import { ConfigurationService } from '../../../services/configuration.service';
import { AppPermissionService } from '../../../services/app-permission.service';
import { AiQuoteChatService } from '../../../services/ai-quote-chat.service';
import { ChatService } from '../../../services/chat.service';
import { AiQuoteChatDataResponse, AiQuoteChatResponse, Quote, QuoteFormRequest, SimpleQuoteFormRequest } from '../../../models/ai-quote-chat.models';
import { QuestionType, ResponseType } from '../../../enums/ai-quote-chat.enum';
import { AiQuoteChatInputComponent } from '../ai-quote-chat-input/ai-quote-chat-input.component';
import { SupportData } from '../../../models/support.models';

const MESSAGE_FIELD = "message";

@Component({
    selector: 'app-ai-quote-chat',
    templateUrl: './ai-quote-chat.component.html',
    styleUrls: ['./ai-quote-chat.component.scss'],
    providers: [ChatService, ChatStreamService],
    animations: [
        trigger('fadeInOut', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(500)
            ]),
            transition(':leave', [
                animate(100, style({ opacity: 0 }))
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AiQuoteChatComponent implements OnInit {
    @Input() isMobileApp: boolean;
    @Input() isAndroid: boolean = false;
    @Input() config: ChatConfigRequest;
    @Input() bearerToken: string;

    @Output() chatEvent = new EventEmitter<ChatEvent>();
    @Output() close = new EventEmitter<string>();
    @Output() clickSupport = new EventEmitter();
    @Output() minimized = new EventEmitter();
    @Output() expanded = new EventEmitter();
    @Output() errorMessageHandler = new EventEmitter<string>();
    @Output() quoteFinalized = new EventEmitter<Quote>();

    @ViewChild('chatContentElement') private chatContentElement: ElementRef;
    @ViewChild('chatInput') private chatInput: AiQuoteChatInputComponent;

    private scrollToBottomQueued = false;

    maximize: boolean = false;
    collapse: boolean = false;

    loading: boolean = false;
    feedbackState: FeedbackState;

    messages: Message[] = [];
    activeMessage: Message;
    activeStreamMessage: Message | null = null;
    activeStatusMessageId: string | null = null;

    initialMessages = [];

    chatInputPlaceholderText: string = "Ask me anything";

    showFeedbackPrompt: boolean;

    // Text & Speech options
    speechEnabled: boolean = false;
    chatStatus: ChatStatus = ChatStatus.CHAT_INIT;

    resetLastActiveTimeOutInterval: ReturnType<typeof setInterval> | undefined;
    awaitingReplyTime = new Date();

    IdleState = IdleStatusType;
    disableInput: boolean = false;
    websocketUrl: string;
    imagePreviewOpen: boolean = false;
    cameraDialogOpen: boolean = false;
    private destroy$ = new Subject<void>();

    showFilePreview: boolean = false;
    filesForPreview: {
        files: { url: string; name: string }[];
        clickedImageIndex: number; // index of the clicked image
    } | null = null;
    
    private readonly PENDING_UPLOAD_FINAL_PROGRESS = 60;
    private readonly PENDING_UPLOAD_FINAL_ANIMATION_STEP_MS = 50;
    private pendingFinalAnimationTimers = new Map<ChatAttachment, any>();
    isFileUploadInProgress = false;

    constructor(
        private sanitizer: DomSanitizer,
        private aiQuoteChatService: AiQuoteChatService,
        private markdownService: MarkdownService,
        private configService: ConfigurationService,
        private authenticationService: AuthenticationService,
        private appPermissionService: AppPermissionService,
        private chatService: ChatService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.loading = true;
        if (this.bearerToken) {
            this.authenticationService.bearerToken = this.bearerToken;
        }

        this.aiQuoteChatService.initializeSession().pipe(
            takeUntil(this.destroy$),
            tap((response) => {
                this.chatStatus = ChatStatus.CHAT_ACTIVE;
                this.aiQuoteChatService.setSessionId(response.conversationId);
                this.aiQuoteChatService.setQuoteId(response.quoteId);
                this.loading = false;
            }),
            tap((response) => {
                this.aiQuoteChatService
                    .connectToAiQuoteStream(response.conversationId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (data) => {
                            this.handleAgentResponse(data);
                        },
                        error: (err) => {
                            this.handleStreamError(err);
                        },
                        complete: () => {
                            console.log('SSE Stream completed');
                        }
                    });
            })
        ).subscribe(() => {
        }, (error: any) => {
            this.loading = false;
            this.handleErrorResponse(error);
        });

        this.scrollToBottom();
    }

    get isFacilisightPortal(): boolean {
        return this.configService.getConfig("appName") === HostRoute.FACILISIGHT;
    }

    get selectedFiles(): ChatAttachment[] | null {
        return this.chatInput?.selectedFiles ?? [];
    };

    get submittedFilesSizeBytes(): number {
        return this.messages.reduce((total, message) => {
            const attachments = message?.data?.attachments ?? [];
            return total + attachments.reduce((attachmentTotal, attachment) => {
                return attachmentTotal + (attachment?.sizeInBytes || 0);
            }, 0);
        }, 0);
    }

    /**
     * Invoked when the user selects an option from the quick select menu.
     * Sets the input text based on the selected option.
     * If the option is vision-related, open the file upload menu.
     * If the option contains an entity reference, also open the mentions menu.
     * Otherwise, auto-submit the message.
     * @param option 
     */
    onSelectOption(option: ChatOption) {
        let placeholderText = option.userInput;
        if (placeholderText.includes(ReservedTags.ENTITY_REF)) {
            placeholderText = placeholderText.replace(ReservedTags.ENTITY_REF, "@");
        }

        if (option.type == SkillType.LINK) {
            const data = option?.presentationData ?? {};
            switch (data?.linkType) {
                case LinkType.EXTERNAL:
                    this.handleExternalLinkNavigation('quick_option', data.link);
                    break;

                case LinkType.INTERNAL:
                    //Needs to be handled
                    break;
            }
        } else {
            if (option.presentationData?.openFileUploadMenu) {
                this.chatInput.showUploadOptions = true; // show the file upload menu.
                // opens directly default (ios/android) upload file menu for mobile app.
                if (this.isMobileApp) {
                    this.chatInput.onClickUpload();
                } else {
                    this.chatInput.setInputText(placeholderText);
                }
            } else {
                this.chatInput.setInputText(placeholderText);
                this.chatInput.showUploadOptions = false;
                // auto submit for non-image related quick select options.
                this.onSubmit();
            }
        }
    }

    // Handle the external links naviagtion (Web/Portal)
    handleExternalLinkNavigation(from: string, url: string) {
        if (this.isMobileApp) {
            this.onMobileCitationLinkClicked({
                from: from,
                url
            });
        } else {
            window.open(url, '_blank');
        }
    }

    onCollapse() {
        this.collapse = true;
        if (this.isMobileApp) {
            this.minimized.emit();
        }
    }

    onReset() {
        this.maximize = false;
    }

    onExpand() {
        this.collapse = false;
        if (this.isMobileApp) {
            this.expanded.emit();
        }
    }

    /** Restores the chat from a collapsed/minimized state when requested by the host app. */
    restoreFromMinimized(): void {
        if (!this.collapse) {
            return;
        }
        this.onExpand();
        this.cdr.detectChanges();
    }

    onMaximize() {
        this.maximize = true;
    }

    /**
     * Close the chat window and invoke the API to perform session cleanup 
     */
    onClose() {
        this.clearTimeOutIntervals();
        this.close.emit(this.aiQuoteChatService.getSessionId());
    }

    clearTimeOutIntervals() {
        clearInterval(this.resetLastActiveTimeOutInterval);
    }

    /**
     * Enable feedback tooltip when the user hovers over the AI chat message.
     * Only enabled for AI messages and web applications.
     * @param message AI Message
     */
    onMouseOver(message: Message) {
        if (message.role == Role.AI) {
            message._metadata!.showFeedbackPrompt = true;
        }
    }

    /**
     * Only disable feedback tooltip on mouse-leave if feedback is inactive OR once feedback has been completed.
     * @param message AI Message
     */
    onMouseLeave(message: Message) {
        if ((message._metadata!.feedbackState == FeedbackState.SUBMIT_SENTIMENT || message._metadata!.feedbackState == FeedbackState.CANCEL)) {
            this.closeFeedback(message);
        }
    }

    onFeedbackChange(event: FeedbackState) {
        this.feedbackState = event;
    }

    onSubmit() {
        const inputText = this.chatInput.getInputText();
        if (inputText.length > 0 || this.selectedFiles?.length) {
            this.submitMessage(inputText);
            this.chatInput.resetFileUpload();
        }
    }

    submitMessageToAgent(userMessage: string, formSubmit: boolean = false): Observable<AiQuoteChatResponse> {
        const payload = {
            [MESSAGE_FIELD]: userMessage
        };

        // If there are no files -> Normal flow -> Send the message to AI
        if (!this.selectedFiles?.length) {
            if (!formSubmit) {
                this.addMessage(this.newUserMessage(userMessage, []));
            }
            return this.aiQuoteChatService.submitMessageToAI(payload);
        }

        // If there are files -> File Upload flow -> No need to send the message to AI
        this.loading = false;
        const attachments = [...this.selectedFiles];
        const files = attachments
            .map((attachment) => attachment.file)
            .filter((file): file is File => !!file);

        attachments.forEach((attachment) => {
            attachment.uploadState = 'uploading';
            attachment.uploadProgress = 0;
        });      
        this.isFileUploadInProgress = true;
        this.addMessage(this.newUserMessage(userMessage ?? '', attachments));
        this.cdr.detectChanges();
        
        this.aiQuoteChatService.uploadAiQuoteFiles(
            {   
                userMessage: userMessage ?? '',
                quoteId: this.aiQuoteChatService.getQuoteId(),
                images: files
            },
            (completed, total, stage, urls, fileIndex, filePercent) => {
                if (stage === 'file-progress' && fileIndex !== undefined && filePercent !== undefined) {
                    attachments[fileIndex].uploadProgress = filePercent;
                    attachments[fileIndex].uploadState = 'uploading';
                    this.cdr.detectChanges();
                    return;
                }

                this.updateAttachmentUploadProgress(attachments, completed, total, urls);
            }
        ).pipe(
            finalize(() => {
                this.isFileUploadInProgress = false;
            })
        ).subscribe({
            next: () => {
                this.loading = true;
                // Force the loader into the DOM now so the queued scroll below can find and reveal it.
                this.cdr.detectChanges();
                this.queueScrollToBottom();
                // Files uploaded successfully; ask the agent to resend its last question now that attachments are attached.
                this.aiQuoteChatService.submitMessageToAI({ [MESSAGE_FIELD]: 'REPEAT_QUESTION' })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError((err) => {
                            this.handleErrorResponse(err);
                            return EMPTY;
                        })
                    )
                    .subscribe();
            },
            error: (err) => {
                this.handleErrorResponse(err);
            }
        });

        return EMPTY;
    }

    submitMessage(userMessage: string, formSubmit: boolean = false) {
        this.loading = true;
        // Force the loader into the DOM now so the queued scroll below can find and reveal it.
        this.cdr.detectChanges();
        this.queueScrollToBottom();
        this.submitMessageToAgent(userMessage, formSubmit).pipe(
            takeUntil(this.destroy$),
            catchError((err) => {
                this.handleErrorResponse(err);
                this.loading = false;
                return EMPTY;
            })
        ).subscribe();
    }

    closeFeedback(message: Message) {
        (message._metadata as MessageMetadata).showFeedbackPrompt = false;
    }

    onClickHelp() {
        if (this.isMobileApp) {
            this.clickSupport.emit();
        } else {
            const chatSupportUrl = this.configService.getConfig("chatSupportUrl");
            window.open(chatSupportUrl, "_blank");
        }
    }

    // Emit event to open citation link in-app browsers for mobile
    onMobileCitationLinkClicked(event: { from: string; url: string }) {
        this.clickSupport.emit(event);
    }

    onToggleSpeech() {
        this.speechEnabled = !this.speechEnabled;
        if (!this.speechEnabled && this.player) {
            this.player.pause();
        }
    }

    onToggleMic() {
        if (this.isMobileApp) {
            if (this.micEnabled) {
                this.appPermissionService.receiveAppMicrophonePermissionStatus().pipe(take(1)).subscribe((status) => {
                    if (status) {
                        this.recognizeSpeech();
                    } else {
                        this.chatInput.micEnabled = false;
                    }
                });
                this.appPermissionService.requestAppMicrophonePermission().next(true);

            } else {
                this.chatInput.micEnabled = false;
            }
        } else {
            if (this.micEnabled) {
                this.recognizeSpeech();
            }
        }
    }

    get micEnabled(): boolean {
        return this.chatInput?.micEnabled ?? false;
    }

    recognizeSpeech() {
        this.chatService.speechConfig.pipe(takeUntil(this.destroy$)).subscribe((speechConfig) => {
            const audioConfig = AudioConfig.fromDefaultMicrophoneInput();
            const speechRecognizer = new SpeechRecognizer(speechConfig, audioConfig);

            speechRecognizer.recognizeOnceAsync(result => {
                if (result.reason === ResultReason.RecognizedSpeech) {
                    this.submitMessage(result.text);
                } else {
                    console.error('Speech was cancelled or could not be recognized. Ensure your microphone is working properly.', result);
                }
                speechRecognizer.close();
                this.micDisabled();
            }, err => {
                this.micDisabled();

            });
        }, err => {
            this.micDisabled();
        })
    }

    micDisabled() {
        this.chatInput.micEnabled = false;
    }

    player: SpeakerAudioDestination;
    speechSynthesizer: SpeechSynthesizer;
    synthesizeSpeech(ai_response: string, language: string) {
        //If the player is already playing, pause it.
        if (this.player && this.speechSynthesizer) {
            this.player.pause();
        }
        this.player = new SpeakerAudioDestination();
        const audioConfig = AudioConfig.fromSpeakerOutput(this.player);
        this.chatService.speechConfig.pipe(takeUntil(this.destroy$)).subscribe((speechConfig) => {
            speechConfig.speechSynthesisLanguage = language;
            this.speechSynthesizer = new SpeechSynthesizer(speechConfig, audioConfig);
            this.speechSynthesizer.speakTextAsync(ai_response, result => {
                if (result) {
                    this.speechSynthesizer.close();
                }
            },
                error => {
                    console.error(error);
                    this.speechSynthesizer.close();
                })
        })
    }

    /**
     * Determines if a message can prompt the user for feedback.
     * @param message Chat message
     * @returns boolean
     */
    canShowFeedback(message: Message) {
        return message._metadata!.showFeedbackPrompt && message.role !== Role.LIVE_AGENT && !this.isMobileApp;
    }

    /**
     * Opens image in a new tab
     * @param imageUrl 
     */
    openImageInNewTab(imageUrl: string | SafeUrl) {
        if (typeof imageUrl === 'object') {
            window.open(this.sanitizer.sanitize(4, imageUrl) || '', '_blank');
        } else {
            window.open(imageUrl, '_blank');
        }
    }

    /**
     * Opens the file 
     * @param attachments 
     * @param clickedAttachment 
     * @returns 
     */
    openAttachmentFilePreview(attachments: ChatAttachment[] | undefined, clickedAttachment: any) {
        const previewFiles = (attachments ?? []).map(attachment => ({
            url: attachment.preview,
            name: attachment.file.name
        }));

        if (!previewFiles.length) {
            return;
        }

        const clickedIndex = Math.max(0,
            (attachments ?? []).findIndex(
                attachment => attachment === clickedAttachment
            )
        );

        this.filesForPreview = {
            files: previewFiles,
            clickedImageIndex: clickedIndex
        };

        this.showFilePreview = true;
    }

    closePreview() {
        this.showFilePreview = false;
        this.filesForPreview = null;
    }

    // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window.
    onScroll(event: WheelEvent): void {

        // Allow scroll within horizontal scroll containers (e.g., resource cards container)
        const horizontalScrollTarget = (event.target as HTMLElement).closest(
            '.resource-cards-container, .strip-scroller'  // your horizontal scroll container selector
        );
        if (horizontalScrollTarget) return

        const chatContent = this.chatContentElement.nativeElement;
        const scrollMargin = 2;
        // Check if the user is at the top or bottom of the chat-messages
        const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
        const isAtTop = chatContent.scrollTop <= scrollMargin;

        if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
            // Prevent background scroll when reached top or bottom of the chat window
            event.preventDefault();
        } else {
            chatContent.scrollTop += event.deltaY; // Ensure the scroll is contained within chat window
            event.preventDefault(); // Preventing the event propagation
        }
    }

    // Method to restrict the background scroll when scroll reaches the top or bottom of the chat window for mobile.
    onTouchMove(event: TouchEvent): void {

        // Let horizontal scroll containers handle their own touch scroll (e.g., resource cards container)
        const horizontalScrollTarget = (event.target as HTMLElement).closest(
            '.resource-cards-container, .strip-scroller'  // your horizontal scroll container selector
        );
        if (horizontalScrollTarget) return

        const chatContent = this.chatContentElement.nativeElement;
        const scrollMargin = 2;
        const isAtBottom = chatContent.scrollHeight - chatContent.scrollTop - chatContent.clientHeight <= scrollMargin;
        const isAtTop = chatContent.scrollTop <= scrollMargin;

        const touchStart = event.touches[0].clientY;
        const touchEnd = event.changedTouches[0]?.clientY || touchStart;
        const touchDirection = touchStart - touchEnd;
        if ((isAtTop && touchDirection < 0) || (isAtBottom && touchDirection > 0)) {
            event.preventDefault();
        } else {
            chatContent.scrollTop += touchDirection;
        }
    }

    // Method to restrict the scroll behavior of the chat container.
    onContainerScroll(event: Event) {
        const target = event.target as HTMLElement;

        // Allow scrolling within these containers
        if (
            target.closest('.chat-messages') ||
            target.closest('.mention-item') ||
            target.closest('.resource-cards-container') ||
            target.closest('.strip-scroller') ||
            target.closest('.desktop-textarea-row')
        ) {
            return;
        }

        event.preventDefault();
    }

    /** Handles the agent's response based on its type. */
    private async handleAgentResponse(response: AiQuoteChatResponse): Promise<any> {
        if (!response) return;
        switch (response.type) {
            case ResponseType.ANSWER:
                return this.handleAnswerResponse(response);
            case ResponseType.FINALIZE:
                return this.handleFinalizeResponse();
            case ResponseType.KNOWLEDGE_QUERY:
                // First handle the knowledge query response
                await this.handleKnowledgeQueryResponse(response);
                // Then handle the next question response which is part of the knowledge.
                this.handleAnswerResponse(response);
                return;
            default:
                this.loading = false;
                return;
        }
    }

    /* Handles answer responses from the agent, which contain the actual response to the user's query. 
        This can include text, follow-up questions, or form requests.
    */
    private async handleAnswerResponse(response: AiQuoteChatResponse) {
        const answer = response?.data as AiQuoteChatDataResponse ?? {};
        
        if (!answer) {
            this.loading = false;
            return;
        }

        // Form responses
        if (answer.questionType == QuestionType.FORM) {
            if (answer?.form) {
                const quoteFormRequest: QuoteFormRequest = answer?.form;
                this.addMessage(this.newAIMessage({ quoteFormRequest }), false);
            }
        } else if (answer.questionType == QuestionType.SINGLE_SELECT || answer.questionType == QuestionType.MULTI_SELECT || answer.questionType == QuestionType.BOOLEAN) {
            const simpleQuoteFormRequest: SimpleQuoteFormRequest = {
                title: answer?.nextQuestion || '' || answer?.message,
                questionType: answer.questionType,
                options: Array.isArray(answer?.responseOptions) ? answer.responseOptions : [],
                submitLabel: 'SUBMIT',
                cancelLabel: 'NOT REQUIRED'
            };
            this.addMessage(this.newAIMessage({ simpleQuoteFormRequest }), false);
        } else {
            // Display the answer message
            if (answer?.message) {
                const messageMarkdown: any = await this.markdownService.parse(answer.message);
                this.addMessage(this.newAIMessage({ text: messageMarkdown }));
            }

            if (answer?.nextQuestion) {
                const nextQuestionMarkdown: any = await this.markdownService.parse(answer.nextQuestion);
                this.addMessage(this.newAIMessage({ text: nextQuestionMarkdown }));
            }
            
            this.chatInput?.focus();
        }

        this.loading = false;
    }

    /* Handles finalize responses from the agent, which indicate that the quote is ready for review.*/
    private async handleFinalizeResponse() {
        const message = 'All set! Your quote is ready for review.\nYou can now open the Quote Builder to make any final edits and submit it to your distributor or sales contact.';
        const messageMarkdown: any = await this.markdownService.parse(message);
        this.addMessage(this.newAIMessage({ text: messageMarkdown }), false, true);
        this.loading = false;
    }

    /* Handles knowledge query responses from the agent, which provide additional context or information. */
    private async handleKnowledgeQueryResponse(response: AiQuoteChatResponse) {
        const answer = response?.data as AiQuoteChatDataResponse ?? {};
        const supportResponse = answer?.knowledgeResponseDto as SupportData;
        if (!supportResponse) {
            return;
        }

        const markdown: any = await this.markdownService.parse(supportResponse.content ?? '');
        const supportData: SupportData = {
            ...supportResponse,
            content: markdown
        };

        this.addMessage(this.newAIMessage({ supportData }));
    }

    /* Handles stream errors from the agent, which can occur due to network issues or inactivity.*/
    private handleStreamError(error: any) {
        const errorMessage = error?.error?.message ?? error?.message ?? '';
        console.error('Stream error:', errorMessage);
        this.loading = false;
    }

    isActiveStreamMessage(message: Message): boolean {
        return !!message._metadata.isStreamMessage && this.activeMessage === message;
    }
    private addMessage(message: Message, isStreamMessage: boolean = false, isFinalizeMessage: boolean = false) {
        message._metadata.isStreamMessage = isStreamMessage;
        message._metadata.isFinalizeMessage = isFinalizeMessage;
        this.activeMessage = message;
        this.messages.push(message);
        this.queueScrollToBottom();
    }

    private handleErrorResponse(error: any): void {
        this.loading = false;
        this.addMessage(this.newAIMessage({ text: error?.error?.detail ?? "Sorry! Please try again." }));
    }

    private newUserMessage(text: string, attachments?: ChatAttachment[] | null, messageId: string = uuid.v4()): Message {
        const data: MessageData = { text, attachments: attachments ?? [] };
        return {
            role: Role.USER,
            data,
            _metadata: {
                messageId,
                showFeedbackPrompt: false
            }
        };
    }

    private newAIMessage(data: MessageData, messageId: string = uuid.v4()): Message {
        const message = { role: Role.AI, data: { ...data }, _metadata: { messageId: messageId, showFeedbackPrompt: false } }
        return message;
    }

    // Updates the upload progress of attachments based on the number of completed uploads and total uploads.
    private updateAttachmentUploadProgress(
        attachments: ChatAttachment[],
        completedUploads: number,
        totalUploads: number,
        uploadedUrls?: string[]
    ): void {
        const progressPercent = totalUploads > 0
            ? Math.round((completedUploads / totalUploads) * 100)
            : 0;

        attachments.forEach((attachment, idx) => {
            const hasUrl = Array.isArray(uploadedUrls) && !!uploadedUrls[idx];

            if (hasUrl) {
                attachment.preview = uploadedUrls![idx];

                // Cancel any existing animation for this attachment
                const existingTimer = this.pendingFinalAnimationTimers.get(attachment);
                if (existingTimer) {
                    clearTimeout(existingTimer);
                    clearInterval(existingTimer);
                    this.pendingFinalAnimationTimers.delete(attachment);
                }

                const start = Math.max(attachment.uploadProgress || 0, this.PENDING_UPLOAD_FINAL_PROGRESS);
                const stepMs = this.PENDING_UPLOAD_FINAL_ANIMATION_STEP_MS;

                // Hold at threshold for 1s before animating to 100%
                attachment.uploadProgress = start;
                attachment.uploadState = 'uploading';
                this.cdr.detectChanges();

                const holdMs = 1000;
                const finalDuration = 500;
                const steps = Math.max(1, Math.ceil(finalDuration / stepMs));
                const delta = 100 - start;

                const holdTimer = setTimeout(() => {
                    let currentStep = 0;
                    const interval = setInterval(() => {
                        currentStep++;
                        const eased = start + Math.round((delta * currentStep) / steps);
                        attachment.uploadProgress = Math.min(100, eased);
                        this.cdr.detectChanges();

                        if (currentStep >= steps) {
                            clearInterval(interval);
                            this.pendingFinalAnimationTimers.delete(attachment);
                            attachment.uploadProgress = 100;
                            attachment.uploadState = 'uploaded';
                            this.cdr.detectChanges();
                        }
                    }, stepMs);

                    this.pendingFinalAnimationTimers.set(attachment, interval);
                }, holdMs);

                this.pendingFinalAnimationTimers.set(attachment, holdTimer);
            } else {
                // In-flight: cap proportional progress at the threshold
                const target = Math.min(
                    this.PENDING_UPLOAD_FINAL_PROGRESS,
                    Math.max(attachment.uploadProgress || 1, progressPercent)
                );
                attachment.uploadProgress = target;
                attachment.uploadState = 'uploading';
            }
        });

        this.cdr.detectChanges();
    }

    private scrollToBottom(): void {
        const element = this.chatContentElement?.nativeElement;
        if (element == null) {
            return;
        }

        // While the typing/loading indicator is showing, always reveal it rather than pinning to the message above it.
        if (!element.querySelector('.loader')) {
            // If the newest message is taller than the visible chat area, show its start instead of jumping to the very bottom.
            const messageContainers: NodeListOf<HTMLElement> = element.querySelectorAll('.chat-message-container');
            const lastMessageEl = messageContainers[messageContainers.length - 1];
            if (lastMessageEl && lastMessageEl.offsetHeight > element.clientHeight) {
                // offsetTop is relative to the nearest positioned ancestor (not necessarily this scroll container),
                // so measure the gap via bounding rects to get a position relative to the scrollable element itself.
                const relativeTop = lastMessageEl.getBoundingClientRect().top - element.getBoundingClientRect().top + element.scrollTop;
                element.scrollTop = relativeTop;
                return;
            }
        }

        element.scrollTop = element.scrollHeight;
    }

    private queueScrollToBottom(): void {
        if (this.scrollToBottomQueued) {
            return;
        }

        this.scrollToBottomQueued = true;
        requestAnimationFrame(() => {
            this.scrollToBottomQueued = false;
            this.scrollToBottom();
        });
    }

    // Submit form data to the agent as a single string
    onFormSubmit(formSelectedValue: any) {
        this.submitMessage(formSelectedValue, true);
    }

    // Triggers the error message emitter to display error messages
    onErrorMessage(message: string) {
        this.errorMessageHandler.emit(message);
    }

    // Returns a CSS conic-gradient string to visually represent the upload progress of a file.
    getUploadProgressStyle(progress: number): string {
        const clampedProgress = Math.max(0, Math.min(100, progress || 0));
        return `conic-gradient(var(--primary) 0deg ${clampedProgress * 3.6}deg, #C9C9C9 ${clampedProgress * 3.6}deg 360deg)`;
    }

    // Finalizes the quote and opens the quote builder for the user to review and submit.
    onFinalizeQuote() {
        this.aiQuoteChatService.finalizeQuote(this.aiQuoteChatService.getSessionId())
            .pipe(takeUntil(this.destroy$)).subscribe(quote => {
                this.quoteFinalized.emit(quote);
            });
    }

    // Tracks the open/close state of the camera dialog based on the isOpen parameter.
    onCameraDialogOpenChange(isOpen: boolean) {
        this.cameraDialogOpen = isOpen;
    }
}
